import { middlewareGateway } from '~/gateway/middleware.gateway'
import { GeocodeResponse } from '~/types/Google'

/**
 * A composable function that provides geolocation and geocoding functionalities using Google APIs.
 *
 * @returns {Object} An object containing methods and reactive properties for geolocation and geocoding.
 * @returns {Function} getGeocoding - A function that takes an address string and fetches geocoding data.
 * @returns {Function} getGeolocation - A function that takes latitude and longitude and fetches geolocation data.
 * @returns {ComputedRef<GeocodeResponse>} result - A computed reference to the geocoding or geolocation result.
 * @returns {ComputedRef<boolean>} loading - A computed reference indicating whether a request is in progress.
 * @returns {ComputedRef<any>} error - A computed reference to any error that occurred during the request.
 */
export default function useGoogleGeoLocation() {
  const nuxtApp = useNuxtApp()
  const locale = nuxtApp.$i18n.locale.value
  const country = localeConfig[locale]?.country

  const result = ref<GeocodeResponse>({
    results: [],
    status: '',
  })
  const loading = ref(false)
  const error = ref()

  /**
   * Fetches geocoding information for a given address using the Google Geocoding API.
   *
   * @param {string} address - The address to geocode.
   * @returns {Promise<void>} A promise that resolves when the geocoding data is fetched.
   *
   * @remarks
   * This function sets the `loading` state to `true` while the request is in progress and
   * sets it back to `false` once the request is completed. If an error occurs during the
   * request, it sets the `error` state with the error information and logs the error to
   * the console. If the request is successful, it updates the `result` state with the
   * geocoding data.
   *
   * @example
   * ```typescript
   * await getGeocoding('4207');
   * console.log(result.value); // Logs the geocoding data for the given address
   * ```
   */
  async function getGeocoding(address: string) {
    try {
      loading.value = true
      const queryParams = `address=${encodeURI(address)}&region=${country}`
      const data = await middlewareGateway<GeocodeResponse>(`/next-gen/google/location/geocoding?${queryParams}`)
      if (!data) return
      result.value = data
      if (data.error_message) {
        error.value = data.error_message
      }
    } catch (e) {
      error.value = e
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetches geolocation data based on the provided latitude and longitude.
   * If no latitude and longitude are provided, it fetches the geolocation data for the user's current location.
   *
   * @param {number} lat - The latitude of the location. Optional.
   * @param {number} lng - The longitude of the location. Optional.
   * @returns {Promise<void>} A promise that resolves when the geolocation data is fetched.
   *
   * @remarks
   * This function sets the `loading` state to `true` while fetching the data and sets it back to `false` once the operation is complete.
   * If an error occurs during the fetch operation, it is caught and assigned to the `error` state, and logged to the console.
   * If the fetch operation is successful, the resulting data is assigned to the `result` state.
   */
  async function getGeolocation(lat?: number, lng?: number) {
    try {
      loading.value = true
      const queryParams = lat != null && lng != null ? `lat=${lat}&lng=${lng}` : ''
      const data = await middlewareGateway<GeocodeResponse>(
        `/next-gen/google/location/geolocation${queryParams ? `?${queryParams}` : ''}`,
      )
      if (!data) return
      result.value = data
      if (data.error_message) {
        error.value = data.error_message
      }
    } catch (e) {
      error.value = e
    } finally {
      loading.value = false
    }
  }
  return {
    getGeocoding,
    getGeolocation,
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  }
}
